import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Send, X, Loader2, Clock } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canAccess } from "../lib/sections";
import { TemplatePicker, PLACEHOLDER_HINT } from "./MessageTemplates";
import { DateInput } from "./DateInput";
import { BroadcastHistory } from "./BroadcastHistory";
import { SearchSelect } from "./SearchSelect";
import { ScheduledBroadcasts, localDT } from "./ScheduledBroadcasts";

const AGENT_CHANNELS = ["rh", "commercial", "exploitation"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const box = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white";

const Recipients = ({ list, picked, setPicked }) => {
  if (list === null) return <p className="text-sm text-slate-500">Choisissez un site.</p>;
  if (!list.length) return <p className="text-sm text-slate-500" data-testid="broadcast-no-recipient">Aucun agent destinataire pour cette sélection.</p>;
  const all = picked.length === list.length;
  return (
    <div className="border border-slate-200 rounded-md">
      <label className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 text-xs font-bold text-slate-600 bg-slate-50">
        <input type="checkbox" data-testid="broadcast-toggle-all" checked={all} onChange={() => setPicked(all ? [] : list.map((a) => a.id))} />
        {picked.length} / {list.length} agent{list.length > 1 ? "s" : ""} sélectionné{picked.length > 1 ? "s" : ""}
      </label>
      <div className="max-h-44 overflow-y-auto">
        {list.map((a) => (
          <label key={a.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" data-testid={`broadcast-agent-${a.id}`} checked={picked.includes(a.id)}
              onChange={() => setPicked(picked.includes(a.id) ? picked.filter((x) => x !== a.id) : [...picked, a.id])} />{a.name}
          </label>
        ))}
      </div>
    </div>
  );
};

const modesFor = (user) => [
  ["planning", "Planning d'un site"],
  ["site", "Intervenants d'un site"],
  ["my_sites", Array.isArray(user?.site_ids) && user?.role !== "admin" ? "Tous mes sites" : "Tous les sites"],
  ...(user?.role === "admin" ? [["all", "Tous les agents actifs"]] : []),
];

const ModePicker = ({ modes, mode, setMode }) => (
  <div className="grid grid-cols-2 gap-1.5" data-testid="broadcast-modes">
    {modes.map(([k, l]) => (
      <button key={k} type="button" data-testid={`broadcast-mode-${k}`} onClick={() => setMode(k)}
        className={`px-2 py-1.5 rounded-md text-xs font-bold border transition-colors ${mode === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-slate-500"}`}>{l}</button>
    ))}
  </div>
);

export const BroadcastDialog = ({ onClose, initial = {} }) => {
  const today = new Date();
  const { user } = useAuth();
  const modes = modesFor(user);
  const [mode, setMode] = useState(modes.some(([k]) => k === initial.mode) ? initial.mode : "planning");
  const [sites, setSites] = useState([]);
  const [chans, setChans] = useState([]);
  const [f, setF] = useState({ site_id: initial.site_id || "", start: initial.start || iso(today), end: initial.end || iso(new Date(today.getTime() + 6 * 864e5)), channel: "", content: "" });
  const [list, setList] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("new");
  const [later, setLater] = useState(false);
  const [sendAt, setSendAt] = useState(() => localDT(new Date(Math.ceil((Date.now() + 36e5) / 9e5) * 9e5)));
  useEffect(() => {
    http.get("/sites").then((r) => setSites(r.data.filter((s) => !s.archived))).catch((e) => toast.error(formatApiError(e)));
    http.get("/messages/channels").then((r) => { const l = r.data.filter((c) => AGENT_CHANNELS.includes(c.key)); setChans(l); setF((x) => ({ ...x, channel: l[0]?.key || "" })); }).catch((e) => toast.error(formatApiError(e)));
  }, []);
  const needSite = mode === "planning" || mode === "site";
  useEffect(() => {
    if ((needSite && !f.site_id) || (mode === "planning" && (!f.start || !f.end))) { setList(null); return; }
    let live = true;
    setList(null); setPicked([]);
    const req = mode === "planning" ? http.get("/messages/broadcast/recipients", { params: { site_id: f.site_id, start: f.start, end: f.end } })
      : http.get("/messages/broadcast/audience", { params: { scope: mode, ...(mode === "site" ? { site_id: f.site_id } : {}) } });
    req.then((r) => { if (live) { setList(r.data); setPicked(r.data.map((a) => a.id)); } }).catch((e) => { if (live) { setList([]); toast.error(formatApiError(e)); } });
    return () => { live = false; };
  }, [mode, needSite, f.site_id, f.start, f.end]);
  const site = sites.find((s) => s.id === f.site_id);
  const send = async () => {
    setBusy(true);
    try {
      const label = modes.find(([k]) => k === mode)?.[1];
      const ctx = mode === "planning" ? `${site?.name || "Site"} du ${f.start.split("-").reverse().join("/")} au ${f.end.split("-").reverse().join("/")}`
        : mode === "site" ? `Intervenants du site ${site?.name || ""}` : label;
      const extra = mode === "planning" ? { site_id: f.site_id, start: f.start, end: f.end } : mode === "site" ? { site_id: f.site_id } : {};
      const { data } = await http.post("/messages/broadcast", { channel: f.channel, agent_ids: picked, content: f.content, context: ctx, ...extra, ...(later ? { send_at: sendAt } : {}) });
      if (data.scheduled) { toast.success(`Message programmé le ${new Date(data.send_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Paris" })} (heure de Paris) pour ${data.count} agent${data.count > 1 ? "s" : ""}`); setTab("scheduled"); setF((x) => ({ ...x, content: "" })); return; }
      toast.success(`Message envoyé à ${data.sent} agent${data.sent > 1 ? "s" : ""}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 grid place-items-end sm:place-items-center p-0 sm:p-4">
      <div data-testid="broadcast-dialog" className="bg-white w-full sm:max-w-lg max-h-[95vh] overflow-y-auto rounded-t-2xl sm:rounded-xl p-5 space-y-4 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-sky-600" /><h4 className="font-bold text-lg">Message groupé</h4>
          <button type="button" onClick={onClose} data-testid="broadcast-close" className="ml-auto p-1 rounded hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-md text-xs font-bold">
          {[["new", "Nouveau message"], ["scheduled", "Programmés"], ["history", "Historique et lectures"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`broadcast-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 py-1.5 rounded transition-colors ${tab === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
        {tab === "history" ? <BroadcastHistory /> : tab === "scheduled" ? <ScheduledBroadcasts /> : <>
        <ModePicker modes={modes} mode={mode} setMode={setMode} />
        {mode === "site" && <p className="text-[11px] text-slate-500 -mt-2">Agents planifiés, préconisés ou déjà intervenus sur le site, toujours dans la société, hors agents interdits.</p>}
        {mode === "my_sites" && <p className="text-[11px] text-slate-500 -mt-2">Tous les intervenants des sites que vous gérez, hors agents interdits sur chaque site.</p>}
        {needSite && <SearchSelect data-testid="broadcast-site" className={box} value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })}>
          <option value="">Choisir un site…</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SearchSelect>}
        {mode === "planning" && <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-slate-600">Du<DateInput data-testid="broadcast-start" className={`${box} mt-1`} value={f.start} onChange={(e) => setF({ ...f, start: e.target.value, end: f.end < e.target.value ? e.target.value : f.end })} /></label>
          <label className="text-xs font-semibold text-slate-600">Au<DateInput data-testid="broadcast-end" className={`${box} mt-1`} min={f.start} value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} /></label>
        </div>}
        <Recipients list={list} picked={picked} setPicked={setPicked} />
        {chans.length === 0 ? <p className="text-sm text-rose-600" data-testid="broadcast-no-channel">Vous n'avez accès à aucun service de messagerie agent.</p> : (
          <div className="flex flex-wrap gap-2">{chans.map((c) => <button key={c.key} type="button" data-testid={`broadcast-channel-${c.key}`} onClick={() => setF({ ...f, channel: c.key })}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${f.channel === c.key ? "bg-sky-600 text-white border-sky-600" : "border-slate-300 hover:border-slate-500"}`}>{c.label}</button>)}</div>
        )}
        <TemplatePicker current={f.content} onPick={(c) => setF((x) => ({ ...x, content: c }))} />
        <textarea data-testid="broadcast-text" rows={4} maxLength={4000} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} placeholder="Votre message…" className={box} />
        {!f.content.trim() && <p className="text-[11px] text-amber-700 -mt-2" data-testid="broadcast-empty-hint">Rédigez un message pour pouvoir l'envoyer.</p>}
        <p className="text-[11px] text-slate-500 -mt-2">{PLACEHOLDER_HINT}</p>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-2 font-semibold"><input type="checkbox" data-testid="broadcast-later" checked={later} onChange={(e) => setLater(e.target.checked)} />Programmer l'envoi</label>
          {later && <input type="datetime-local" step={900} data-testid="broadcast-send-at" value={sendAt} min={localDT(new Date())} onChange={(e) => setSendAt(e.target.value)} className="border border-slate-300 rounded-md px-2 py-1 text-sm" />}
          {later && <span className="text-[11px] text-slate-500">heure de Paris</span>}
        </div>
        {later && !sendAt && <p className="text-[11px] text-rose-600 -mt-2" data-testid="broadcast-send-at-error">Choisissez la date et l'heure d'envoi.</p>}
        {later && <p className="text-[11px] text-slate-500 -mt-2">Les agents cochés maintenant recevront le message à l'heure choisie (envoi dans le quart d'heure). Vous pourrez le modifier ou l'annuler d'ici là.</p>}
        <button type="button" data-testid="broadcast-send" onClick={send} disabled={busy || !f.content.trim() || !picked.length || !f.channel || (later && !sendAt)}
          className="w-full inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white font-semibold rounded-md py-2.5 text-sm">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (later ? <Clock className="h-4 w-4" /> : <Send className="h-4 w-4" />)}{later ? "Programmer pour" : "Envoyer à"} {picked.length} agent{picked.length > 1 ? "s" : ""}
        </button>
        </>}
      </div>
    </div>
  );
};

export const BroadcastButton = ({ className, initial }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!canAccess(user, "messages")) return null;
  return (
    <>
      <button type="button" data-testid="open-broadcast" onClick={() => setOpen(true)} className={className}><Megaphone className="h-3.5 w-3.5" /> Message groupé</button>
      {open && <BroadcastDialog onClose={() => setOpen(false)} initial={typeof initial === "function" ? initial() : initial} />}
    </>
  );
};
